import CoreBase from '../../../services/engines/CoreBase';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import AppContext from '../../../controllers/AppContext';
import cardPainterManager from '../../../Widgets/DataPainterWidget/Core/CardPainterManager';
import jxWindowCache from '../../../JxControls/JxWindowManager/JxWindowCache';
import jxWindowEngine from '../../../JxControls/JxWindowManager/JxWindowEngine';
import { JxWindowEvents } from '../../../JxControls/JxWindowManager/JxWindowEvents';
import { subscribe } from '../../../services/utils/pubsub';
import tabEngine from '../TabEngine/TabEngine';
import workspaceStore from './WorkspaceStore';
import WorkspaceSnapshotBuilder from './WorkspaceSnapshotBuilder';
import WorkspaceGeoCopier from './WorkspaceGeoCopier';
import WorkspaceTabBuilder from './WorkspaceTabBuilder';
import { WS_DEFAULT_NAME, WS_DEFAULT_RK, WS_DEFAULT_TAB_ID, WS_ENGINE_NAME, WS_SAVE_DEBOUNCE_MS } from './workspaceConst';

/**
 * WorkspaceEngine — 1 active workspace at a time, a tab switch = teardown + rebuild.
 * Auto-saves the open card list on cardsChanged / windowsChanged / pinChanged (debounced,
 * only when changed). Geo is scoped per workspace via AppContext.setWorkspaceRk.
 */
class WorkspaceEngine extends CoreBase {
    _name = WS_ENGINE_NAME;
    _booted = false;
    _activeRk = null;
    _isLoading = false;
    _saveTimer = null;
    _unsubs = [];

    bootup = () => {
        if (this._booted) return;
        this._booted = true;

        this._unsubs = [
            ComponentNotifier.subscribe('TabEngine', this._onTabEvent),
            ComponentNotifier.subscribe('CardPainterManager', this._scheduleSave),
            ComponentNotifier.subscribe('JxWindowManager', this._scheduleSave),
            ComponentNotifier.subscribe('JxWindowCache', this._scheduleSave),
            subscribe(JxWindowEvents.channel, this._onWindowEvent),
        ];

        this.getAll().forEach((ws) => this._addTab(ws));
        const def = workspaceStore.get(WS_DEFAULT_RK);
        if (def) tabEngine.renameTab(WS_DEFAULT_TAB_ID, def.name);
    };

    /**
     * @returns {{rk: string, name: string, isActive: boolean}[]} default first, then saved workspaces
     */
    getAll = () => {
        const saved = workspaceStore.getAll().filter((s) => s.rk !== WS_DEFAULT_RK);
        const def = { rk: WS_DEFAULT_RK, name: workspaceStore.get(WS_DEFAULT_RK)?.name ?? WS_DEFAULT_NAME };
        return [def, ...saved].map((s) => ({ rk: s.rk, name: s.name, isActive: s.rk === this._activeRk }));
    };

    getActiveRk = () => this._activeRk;

    /**
     * Saves the active workspace right away, even when unchanged.
     * @returns {boolean} false when there is no active workspace
     */
    save = () => {
        if (!this._activeRk) return false;
        clearTimeout(this._saveTimer);
        this._saveNow(true);
        return true;
    };

    /**
     * Saves the current layout as a new workspace (incl. geo copy) and switches to it.
     * @param {string} name
     */
    saveAs = async (name) => {
        try {
            const rk = crypto.randomUUID().replace(/-/g, '');
            workspaceStore.save(WorkspaceSnapshotBuilder.build(rk, name));
            await WorkspaceGeoCopier.copyToScope(AppContext.getGeoScopeIdFor(rk));
            this._addTab({ rk, name });
            tabEngine.setTabById(WorkspaceTabBuilder.toTabId(rk));
        } catch (error) {
            this.logError('saveAs', error.message);
        }
    };

    /**
     * Creates an empty workspace (only the Workspaces card, no geo copy) and switches to it.
     * @param {string} name
     */
    createBlank = (name) => {
        try {
            const rk = crypto.randomUUID().replace(/-/g, '');
            workspaceStore.save(WorkspaceSnapshotBuilder.blank(rk, name));
            this._addTab({ rk, name });
            tabEngine.setTabById(WorkspaceTabBuilder.toTabId(rk));
        } catch (error) {
            this.logError('createBlank', error.message);
        }
    };

    /**
     * @param {string} rk
     * @param {string} name
     */
    rename = (rk, name) => {
        const fallback = rk === this._activeRk ? WorkspaceSnapshotBuilder.build(rk, name) : { rk, name, cards: [] };
        workspaceStore.save({ ...(workspaceStore.get(rk) ?? fallback), name });
        tabEngine.renameTab(WorkspaceTabBuilder.toTabId(rk), name);
        this._notify();
    };

    /**
     * Removes a saved workspace, the default workspace can't be removed.
     * @param {string} rk
     */
    remove = (rk) => {
        if (rk === WS_DEFAULT_RK) return;
        workspaceStore.remove(rk);
        tabEngine.removeTab(WorkspaceTabBuilder.toTabId(rk), WS_DEFAULT_TAB_ID);
        this._notify();
    };

    /**
     * Loads a workspace: unmount cards, switch geo scope, fetch geo, re-open cards.
     * @param {string} rk
     */
    activate = async (rk) => {
        try {
            this._isLoading = true;
            clearTimeout(this._saveTimer);
            this._activeRk = rk;

            cardPainterManager.replaceCards([]); // forces remount, so every card re-reads its geo
            await this._waitForTabId();
            AppContext.setWorkspaceRk(rk === WS_DEFAULT_RK ? null : rk);
            jxWindowCache.hydrate([]);
            await jxWindowEngine.fetchGeo();

            const snapshot = workspaceStore.get(rk);
            WorkspaceSnapshotBuilder.applyGeo(snapshot); // snapshot geo wins over BE geo
            WorkspaceSnapshotBuilder.applyWindowStates(snapshot);
            cardPainterManager.replaceCards(snapshot?.cards ?? []);
            this._notify();
        } catch (error) {
            this.logError('activate', error.message);
        } finally {
            setTimeout(() => (this._isLoading = false), WS_SAVE_DEBOUNCE_MS); // ignore mount noise
        }
    };

    clear() {
        this._unsubs.forEach((u) => u?.());
        this._unsubs = [];
        clearTimeout(this._saveTimer);
        this._activeRk = null;
        this._booted = false;
    }

    // ─── private ──────────────────────────────────────────

    _onTabEvent = (e) => {
        if (e.target !== 'tabChanged') return;
        const rk = WorkspaceTabBuilder.toRk(e.data);
        if (!rk || rk === this._activeRk) return;
        this.activate(rk);
    };

    // drag/resize only goes to BE WindowPosition, geo now lives in the snapshot too
    _onWindowEvent = ({ name }) => {
        if (name !== JxWindowEvents.onPosChanged && name !== JxWindowEvents.onSizeChanged) return;
        this._scheduleSave();
    };

    _scheduleSave = () => {
        if (this._isLoading || !this._activeRk) return;
        clearTimeout(this._saveTimer);
        this._saveTimer = setTimeout(this._saveNow, WS_SAVE_DEBOUNCE_MS);
    };

    _saveNow = (force = false) => {
        try {
            const rk = this._activeRk;
            const stored = workspaceStore.get(rk);
            const name = stored?.name ?? WS_DEFAULT_NAME;
            const snap = WorkspaceSnapshotBuilder.build(rk, name);
            if (!force && WorkspaceSnapshotBuilder.isSame(snap, stored)) return;
            workspaceStore.save(snap);
        } catch (error) {
            this.logError('_saveNow', error.message);
        }
    };

    _addTab = (ws) => {
        if (ws.rk === WS_DEFAULT_RK) return;
        const tabs = tabEngine.getVisibleTabs();
        const lastWs = [...tabs].reverse().find((t) => WorkspaceTabBuilder.toRk(t));
        tabEngine.addTab(WorkspaceTabBuilder.toTab(ws), lastWs?.id ?? WS_DEFAULT_TAB_ID);
    };

    // AppContext.init is async — at boot the first activate can run before tabId exists
    _waitForTabId = async () => {
        for (let i = 0; i < 50 && !AppContext.getTabId(); i++) await new Promise((r) => setTimeout(r, 100));
    };

    _notify = () => ComponentNotifier.notify(WS_ENGINE_NAME, 'workspacesChanged', { activeRk: this._activeRk });
}

const workspaceEngine = new WorkspaceEngine();
export default workspaceEngine;
